/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useRef, useState } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip } from "antd";
// REDUX
import { useSelector } from "react-redux";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// CUSTOM HOOKS
import { useLastBankMessage } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import FloorCellIcon from "../iconDefinition/FloorCellIcon";
import LiftDegradedOverlay from "../iconDefinition/LiftDegradedOverlay";
import MonitoringElevatorFullViewName from "./MonitoringElevatorFullViewName";
import IconDefinition from "../iconDefinition/IconDefinition";
import HallCallLines from "./HallCallLines";
// INTERFACES
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface, BankFloorInterface } from "../../../interfaces/Bank.interface";
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
// UTILS
import { stringToUuidT } from "../../../utils/uuid";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { hasMixedDoorConfig } from "../../../utils/bitmaskToIcon";
import formatUuidT from "../../../flatbuffers/display/uuid";

const asArray = <T,>(value?: T[] | null): T[] => (Array.isArray(value) ? value : []);

const normalizePositionCalls = (
  calls?: {
    front?: number[];
    rear?: number[];
    frontLock?: number[];
    rearLock?: number[];
  } | null,
) => ({
  front: asArray(calls?.front),
  rear: asArray(calls?.rear),
  frontLock: asArray(calls?.frontLock),
  rearLock: asArray(calls?.rearLock),
});

function MonitoringElevatorsFullView({
  bank,
  serverId,
  channelId,
  enableActions,
}: {
  bank: BankInterface;
  serverId: string;
  channelId: string;
  enableActions: boolean;
}) {
  const [t] = useTranslation("global");
  const customConfiguration = bank?.adapter?.socket_message_priority?.customConfiguration;

  const [bankId, setBankId] = useState<string>("");

  const theme = useSelector((state: RootState) => state.home.theme);
  const openReplayPanel = useSelector((state: RootState) => state.monitoring.openReplayPanel);

  const bankUuid = useMemo(() => stringToUuidT(bankId), [bankId]);
  const socketMessageBank: SocketMessageInterface =
    useLastBankMessage(bankUuid) || BLANK_SOCKET_MESSAGE;
  const lifts = useMemo(() => asArray(bank?.lifts), [bank?.lifts]);
  const bankFloors = useMemo(() => asArray(bank?.bankFloors), [bank?.bankFloors]);
  const hallCalls = useMemo(
    () => ({
      up: normalizePositionCalls(socketMessageBank?.hallCalls?.up),
      down: normalizePositionCalls(socketMessageBank?.hallCalls?.down),
    }),
    [socketMessageBank],
  );

  // Single ResizeObserver on the hoistway container to set --cell-width
  const hoistRef = useRef<HTMLDivElement>(null);
  const [cellWidth, setCellWidth] = useState<number>(60);

  useEffect(() => {
    const el = hoistRef.current;
    if (!el) return undefined;
    const liftCount = lifts.length || 1;
    const gap = 6;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const containerWidth = entry.contentRect.width;
        const computed = (containerWidth - gap * (liftCount - 1)) / liftCount;
        setCellWidth(Math.max(computed, 20));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [lifts.length]);

  // Dynamic sizes: clamped by both cell width AND cell height (25px)
  const cellHeight = 25;
  const maxIcon = Math.min(cellWidth - 4, cellHeight - 4, 20);
  const iconSize = Math.max(12, maxIcon);
  const badgeSize = Math.max(6, Math.min(cellWidth * 0.3, 10));

  const getFloorLabel = (floor: BankFloorInterface): string => {
    if (floor.name && floor.name.trim().length > 0) {
      return floor.name.toUpperCase().substring(0, 2);
    }
    return String(floor.floorNo);
  };

  const getFloorTooltip = (floor: BankFloorInterface): string => {
    if (floor.name && floor.name.trim().length > 0) {
      return floor.name;
    }
    return `Floor ${floor.floorNo}`;
  };

  const activeHallCalls = useMemo(() => {
    const upFront = new Set(hallCalls.up.front);
    const upRear = new Set(hallCalls.up.rear);
    const downFront = new Set(hallCalls.down.front);
    const downRear = new Set(hallCalls.down.rear);
    const frontLock = new Set([...hallCalls.up.frontLock, ...hallCalls.down.frontLock]);
    const rearLock = new Set([...hallCalls.up.rearLock, ...hallCalls.down.rearLock]);

    return { upFront, upRear, downFront, downRear, frontLock, rearLock };
  }, [hallCalls]);

  useEffect(() => {
    if (bank && bank.id && String(bank.id) !== "") {
      setBankId(String(bank.id));
    }
  }, [bank]);

  const sortedLifts = useMemo(
    () => lifts.slice().sort((a: LiftInterface, b: LiftInterface) => a.position - b.position),
    [lifts],
  );

  const sortedFloors = useMemo(
    () =>
      bankFloors
        .slice()
        .sort((a: BankFloorInterface, b: BankFloorInterface) => b.floorIndex - a.floorIndex),
    [bankFloors],
  );

  // Which bank-floors each lift actually serves (from lift_floors). Lifts in the
  // same bank can serve different floor sets; the shaft renders the shared floor
  // axis, so a cell is a real stop only when THIS lift serves the floor — others
  // render as a "skip" gap. An empty/absent set ⇒ the lift serves all bank floors
  // (no lift_floors data), preserving behaviour for banks without that mapping.
  const liftServedFloorSets = useMemo(() => {
    const byLift = new Map<string, Set<string>>();
    sortedLifts.forEach((lift: LiftInterface) => {
      const set = new Set<string>();
      asArray(lift.liftFloors).forEach((lf) => {
        const id = formatUuidT(lf.bankFloorId);
        if (id) set.add(id);
      });
      byLift.set(String(lift.id ?? ""), set);
    });
    return byLift;
  }, [sortedLifts]);

  const liftServesFloor = (lift: LiftInterface, floor: BankFloorInterface): boolean => {
    const served = liftServedFloorSets.get(String(lift.id ?? ""));
    if (!served || served.size === 0) return true; // no lift_floors data ⇒ serves all
    return served.has(formatUuidT(floor.id));
  };

  // CSS custom property style for the hoistway container
  const hoistStyle = useMemo(
    () =>
      ({
        "--cell-width": `${cellWidth}px`,
        "--icon-size": `${iconSize}px`,
        "--badge-size": `${badgeSize}px`,
        position: "relative",
      }) as React.CSSProperties,
    [cellWidth, iconSize, badgeSize],
  );

  return (
    <div className="monitoring__elevatorFullView__container">
      {sortedFloors.map((floor: BankFloorInterface, floorIndex: number) => {
        const hasUpFront = activeHallCalls.upFront.has(floor.floorIndex);
        const hasUpRear = activeHallCalls.upRear.has(floor.floorIndex);
        const hasDownFront = activeHallCalls.downFront.has(floor.floorIndex);
        const hasDownRear = activeHallCalls.downRear.has(floor.floorIndex);
        const hasAnyUp = hasUpFront || hasUpRear;
        const hasHallCall = hasUpFront || hasUpRear || hasDownFront || hasDownRear;
        const hasMixedDoors = hasMixedDoorConfig(bank.bankFloors);
        return (
          <div key={floor.id}>
            <div className="generalStyles__flex" key={floor.id}>
              {/** LEFT FLOORS / FRONT DOOR FLOORS */}
              <div
                className={`monitoring__card__floor generalStyles__mlFix ${
                  hasHallCall
                    ? hasAnyUp
                      ? `monitoring__card__floor__active__green__${theme}`
                      : `monitoring__card__floor__active__red__${theme}`
                    : floor.frontDoor
                      ? `monitoring__card__floor__door__${theme}`
                      : `monitoring__card__floor__inactive__${theme}`
                }`}
              >
                {activeHallCalls.frontLock.has(floor.floorIndex) ? (
                  <Tooltip title={t("general.frontDoorLocked")}>
                    <div>
                      <IconDefinition
                        isMap={false}
                        expanded={false}
                        liftType={LiftType[LiftType.ELEVATOR]}
                        icon="elLocked"
                        iconSize={13}
                      />
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip title={getFloorTooltip(floor)}>
                    {openReplayPanel ? (
                      <span className="generalStyles__warning">{floor.floorIndex}</span>
                    ) : (
                      <span>{getFloorLabel(floor)}</span>
                    )}
                  </Tooltip>
                )}
              </div>
              {/** DOTS AND ELEVATOR CONTAINER */}
              <div
                className="monitoring__elevatorFullView__dotsContainer"
                ref={floorIndex === 0 ? hoistRef : undefined}
                style={floorIndex === 0 ? hoistStyle : undefined}
              >
                {/** Per-lift degraded overlays (only on first floor row to avoid duplicates) */}
                {floorIndex === 0 &&
                  sortedLifts.map((lift: LiftInterface, liftIndex: number) => (
                    <LiftDegradedOverlay
                      key={`overlay-${lift.id}`}
                      lift={lift}
                      totalFloors={bankFloors.length}
                      liftIndex={liftIndex}
                      liftCount={sortedLifts.length}
                      cellWidth={cellWidth}
                    />
                  ))}
                {/** LIFTS */}
                <div
                  className="monitoring__elevatorFullView__liftContainer"
                  style={{
                    display: "flex",
                    gap: "6px",
                    justifyContent: "center",
                  }}
                >
                  {sortedLifts.map((lift: LiftInterface) => (
                    <div
                      key={String(lift.id ?? "")}
                      style={{
                        flex: "1 1 0",
                        minWidth: 0,
                      }}
                    >
                      <FloorCellIcon
                        floor={floor}
                        lift={lift}
                        bank={bank}
                        floorIndex={floorIndex}
                        frontDoors={floor.frontDoor}
                        rearDoors={floor.rearDoor}
                        bankHasMixedDoors={hasMixedDoors}
                        served={liftServesFloor(lift, floor)}
                        variant={customConfiguration ? "custom" : "default"}
                        adapterRules={
                          customConfiguration
                            ? (bank?.adapter?.socket_message_priority?.rules ?? [])
                            : undefined
                        }
                        iconSize={iconSize}
                        badgeSize={badgeSize}
                      />
                    </div>
                  ))}
                </div>
                {/** DOTS STYLE */}
                <div className="monitoring__elevatorFullView__dotsStyleContainer">
                  <div className={`monitoring__card__dotStyle__${theme}__inactive`} />
                </div>
                {/** HALL CALL LINES — 4 quarter-height stripes, each with its
                    own color so all four can be visible simultaneously and the
                    operator can tell at a glance which direction+side is
                    calling. Solid (no animation) so each line stays lit until
                    its specific signal goes false; the conditional renders are
                    independent so an unrelated state change doesn't re-mount
                    or re-flash an active line. */}
                <HallCallLines
                  hasUpFront={hasUpFront}
                  hasUpRear={hasUpRear}
                  hasDownFront={hasDownFront}
                  hasDownRear={hasDownRear}
                />
              </div>
              {/** RIGHT FLOORS / REAR DOOR FLOORS */}
              <div
                className={`monitoring__card__floor ${
                  hasHallCall
                    ? hasAnyUp
                      ? `monitoring__card__floor__active__green__${theme}`
                      : `monitoring__card__floor__active__red__${theme}`
                    : floor.rearDoor
                      ? `monitoring__card__floor__door__${theme}`
                      : `monitoring__card__floor__inactive__${theme}`
                }`}
              >
                {activeHallCalls.rearLock.has(floor.floorIndex) ? (
                  <Tooltip title={t("general.rearDoorLocked")}>
                    <div>
                      <IconDefinition
                        isMap={false}
                        expanded={false}
                        liftType={LiftType[LiftType.ELEVATOR]}
                        icon="elLocked"
                        iconSize={13}
                      />
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip title={getFloorTooltip(floor)}>
                    {openReplayPanel ? (
                      <span className="generalStyles__warning">{floor.floorIndex}</span>
                    ) : (
                      <span>{getFloorLabel(floor)}</span>
                    )}{" "}
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {/** LIFT NAMES ROW */}
      <div className="generalStyles__flex">
        <div className="monitoring__card__floor generalStyles__mlFix">
          <span />
        </div>
        <div className="monitoring__elevatorFullView__dotsContainer">
          <div
            className="monitoring__elevatorFullView__liftContainer"
            style={{
              display: "flex",
              gap: "6px",
              justifyContent: "center",
            }}
          >
            {sortedLifts.map((lift: LiftInterface) => (
              <div
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  textAlign: "center",
                }}
                key={String(lift.id ?? "")}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <MonitoringElevatorFullViewName
                    lift={lift}
                    bank={bank}
                    serverId={serverId}
                    channelId={channelId}
                    enableActions={enableActions}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="monitoring__card__floor">
          <span />
        </div>
      </div>
    </div>
  );
}

export default MonitoringElevatorsFullView;
